import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type {
  AuthorityCoverage,
  AuthorityInbox,
  AuthorityReview,
  AuthorityTopicCandidate,
  StateSetter,
} from '../types'

// AuthorityContext owns the durable authority workspace state previously
// held in App.jsx as five useState entries plus the `applyAuthorityInbox`
// reducer:
//   - reviews             — durable Claude-backed authority review runs
//   - topicCandidates     — pending/accepted/dismissed/converted proposals
//   - coverage            — the inbox rollup payload
//   - currentReview       — the in-progress run (transient UI state)
//   - reviewLoading       — spinner gate (transient UI state)
//
// Wave 7 Phase C — extracts all five slots and the reducer. The reducer
// fires on every payload (no gating), matching the legacy App.jsx
// behavior where `applyAuthorityInbox(null)` reset the inbox to empty.
// Wave 15 — promoted to TypeScript.

export interface AuthorityContextValue {
  reviews: AuthorityReview[]
  topicCandidates: AuthorityTopicCandidate[]
  coverage: AuthorityCoverage | null
  currentReview: AuthorityReview | null
  reviewLoading: boolean
  setReviews: StateSetter<AuthorityReview[]>
  setTopicCandidates: StateSetter<AuthorityTopicCandidate[]>
  setCoverage: StateSetter<AuthorityCoverage | null>
  setCurrentReview: StateSetter<AuthorityReview | null>
  setReviewLoading: StateSetter<boolean>
  applyInbox: (payload?: AuthorityInbox | null) => void
}

const DEFAULT_INBOX: AuthorityInbox = Object.freeze({
  reviews: [],
  topicCandidates: [],
  coverage: null,
}) as AuthorityInbox

const noop = (): void => {}

const AuthorityContext = createContext<AuthorityContextValue>({
  reviews: [],
  topicCandidates: [],
  coverage: null,
  currentReview: null,
  reviewLoading: false,
  setReviews: noop,
  setTopicCandidates: noop,
  setCoverage: noop,
  setCurrentReview: noop,
  setReviewLoading: noop,
  applyInbox: noop,
})

export interface AuthorityProviderProps {
  children?: ReactNode
  initialState?: {
    reviews?: AuthorityReview[]
    topicCandidates?: AuthorityTopicCandidate[]
    coverage?: AuthorityCoverage | null
    currentReview?: AuthorityReview | null
    reviewLoading?: boolean
  }
}

export function AuthorityProvider({
  children,
  initialState = {},
}: AuthorityProviderProps) {
  const [reviews, setReviews] = useState<AuthorityReview[]>(
    initialState.reviews || []
  )
  const [topicCandidates, setTopicCandidates] = useState<
    AuthorityTopicCandidate[]
  >(initialState.topicCandidates || [])
  const [coverage, setCoverage] = useState<AuthorityCoverage | null>(
    initialState.coverage === undefined ? null : initialState.coverage
  )
  const [currentReview, setCurrentReview] = useState<AuthorityReview | null>(
    initialState.currentReview === undefined ? null : initialState.currentReview
  )
  const [reviewLoading, setReviewLoading] = useState<boolean>(
    Boolean(initialState.reviewLoading)
  )

  // Mirrors the legacy App.jsx `applyAuthorityInbox`: stores all three
  // inbox slots from a payload, falling back to empty defaults when the
  // payload is missing or has missing fields. Always fires — passing
  // null intentionally resets the inbox.
  const applyInbox = useCallback((payload?: AuthorityInbox | null) => {
    const next: AuthorityInbox = payload || { ...DEFAULT_INBOX }
    setReviews(next.reviews || [])
    setTopicCandidates(next.topicCandidates || [])
    setCoverage(next.coverage || null)
  }, [])

  const value = useMemo<AuthorityContextValue>(
    () => ({
      reviews,
      topicCandidates,
      coverage,
      currentReview,
      reviewLoading,
      setReviews,
      setTopicCandidates,
      setCoverage,
      setCurrentReview,
      setReviewLoading,
      applyInbox,
    }),
    [
      reviews,
      topicCandidates,
      coverage,
      currentReview,
      reviewLoading,
      applyInbox,
    ]
  )

  return (
    <AuthorityContext.Provider value={value}>
      {children}
    </AuthorityContext.Provider>
  )
}

export function useAuthority(): AuthorityContextValue {
  return useContext(AuthorityContext)
}

export { DEFAULT_INBOX, AuthorityContext }
